import type { Locale } from "@/i18n/routing";
import type { LocalizedText, OrderItemVariantOptions } from "@/lib/validation/json";

/**
 * What a sold line was configured with, as every surface that lists one shows
 * it: the variant's options first, then the add-ons grouped under their group.
 *
 * ⚠️ Client-safe on purpose: it imports types only, so the cart drawer, the
 * panel's order page and the printed invoices all read through one grouping.
 */

/**
 * The variant's axes as the order line snapshots them, or null when the
 * product records none (a variant typed as one free label).
 *
 * Reads both Json columns defensively: `Product.options` and
 * `ProductVariant.options` are merchant-written and an import can hold
 * anything, and checkout must not throw over a malformed row.
 */
export function variantOptionsSnapshot(
	axes: unknown,
	variantOptions: unknown,
): OrderItemVariantOptions | null {
	if (!Array.isArray(axes) || !variantOptions || typeof variantOptions !== "object") return null;
	const { valuesAr, valuesEn } = variantOptions as { valuesAr?: unknown; valuesEn?: unknown };
	const ar = Array.isArray(valuesAr) ? valuesAr : [];
	const en = Array.isArray(valuesEn) ? valuesEn : [];
	const out: OrderItemVariantOptions = [];
	for (const [i, axis] of axes.entries()) {
		if (!axis || typeof axis !== "object") continue;
		const { nameAr, nameEn } = axis as { nameAr?: unknown; nameEn?: unknown };
		const name = { ar: str(nameAr), en: str(nameEn) };
		const value = { ar: str(ar[i]), en: str(en[i]) };
		if ((name.ar || name.en) && (value.ar || value.en)) out.push({ name, value });
	}
	return out.length > 0 ? out : null;
}

/** One row of a line's configuration: a label and what was picked under it. */
export interface LineOptionRow {
	kind: "variant" | "addons";
	/** The axis or group name; null when the snapshot carries none. */
	name: string | null;
	values: string[];
}

/**
 * A line's variant and add-ons as display rows, localized.
 *
 * The variant contributes one row per axis ("Size: Large"), or one unnamed
 * row holding `variantLabel` when no axes were recorded. Add-ons are grouped
 * by their snapshotted group, in the order the groups first appear; entries
 * with no group (imported orders) collect into one unnamed row. A caller
 * renders an unnamed row under its own generic label, which is what keeps a
 * variant and an add-on apart when neither has a name.
 */
export function lineOptionRows(
	line: {
		variantLabel?: LocalizedText | null;
		variantOptions?: OrderItemVariantOptions | null;
		addons?: { label?: LocalizedText | null; group?: LocalizedText | null }[] | null;
	},
	locale: Locale,
): LineOptionRow[] {
	const rows: LineOptionRow[] = [];
	const axes = Array.isArray(line.variantOptions) ? line.variantOptions : [];
	if (axes.length > 0) {
		for (const o of axes) {
			const value = pick(o.value, locale);
			if (value)
				rows.push({ kind: "variant", name: pick(o.name, locale) || null, values: [value] });
		}
	} else {
		const label = pick(line.variantLabel, locale);
		if (label) rows.push({ kind: "variant", name: null, values: [label] });
	}

	// A repeatable group snapshots one entry per pick; the same label twice in a
	// group reads as one value with its count ("Cola ×2").
	const groups = new Map<string, { row: LineOptionRow; counts: Map<string, number> }>();
	for (const a of Array.isArray(line.addons) ? line.addons : []) {
		const label = pick(a.label, locale);
		if (!label) continue;
		const name = pick(a.group, locale) || null;
		const key = name ?? "";
		let g = groups.get(key);
		if (!g) {
			g = { row: { kind: "addons", name, values: [] }, counts: new Map() };
			groups.set(key, g);
		}
		g.counts.set(label, (g.counts.get(label) ?? 0) + 1);
	}
	for (const { row, counts } of groups.values()) {
		for (const [label, n] of counts) row.values.push(addonValue(label, n));
		rows.push(row);
	}
	return rows;
}

/**
 * A picked add-on with its count when it was picked more than once. The count
 * is isolated (U+2066 … U+2069) so "×2" stays after the label in Arabic.
 */
export function addonValue(label: string, n: number): string {
	return n > 1 ? `${label} \u2066×${n}\u2069` : label;
}

/**
 * `pickLocale`, except that an EMPTY side falls back to the other: a merchant
 * may fill a group or axis name in one language only, and `pickLocale` returns
 * the empty string it finds.
 */
function pick(value: LocalizedText | null | undefined, locale: Locale): string {
	if (!value) return "";
	if (typeof value === "string") return (value as string).trim();
	const other: Locale = locale === "ar" ? "en" : "ar";
	return value[locale]?.trim() || value[other]?.trim() || "";
}

function str(v: unknown): string {
	return typeof v === "string" ? v.trim() : "";
}
